// client/src/components/company/PastVatCreditRequests.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatCurrencyWithSymbol } from '../utils/formatUtils';
import { useResponsive } from '../../hooks/useResponsive';
import { Files } from 'lucide-react';
import '../../styles/VatCreditRequestDetail.css';

const PastVatCreditRequests = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportLoading, setExportLoading] = useState({ excel: false, pdf: false });

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const res = await axios.get('/api/vat-credit-requests');
        const allReqs = res.data.requests || [];
        setRequests(allReqs);
        setFilteredRequests(allReqs);
        setLoading(false);
      } catch (err) {
        setError(t('common.errors.fetchRequests', {
          defaultValue: 'Failed to fetch requests'
        }));
        setLoading(false);
      }
    };

    fetchRequests();
  }, [t]);

  useEffect(() => {
    if (activeTab === 'all') {
      setFilteredRequests(requests);
    } else if (activeTab === 'bidding') {
      setFilteredRequests(requests.filter(request =>
        request.status === 'bidding' ||
        request.status === 'pending_allocation'
      ));
    } else {
      setFilteredRequests(requests.filter(request => request.status === activeTab));
    }
  }, [activeTab, requests]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleExport = async (format) => {
    if (!filteredRequests || filteredRequests.length === 0) {
      alert(t('requests.noDataToExport', { defaultValue: 'No data available for export' }));
      return;
    }

    setExportLoading(prev => ({ ...prev, [format]: true }));

    try {
      const response = await axios.get(`/api/vat-credit-requests/export/${format}`, {
        params: {
          status: activeTab !== 'all' ? activeTab : undefined
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response header or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Past_VAT_Credit_Requests_${activeTab}_${Date.now()}.${format}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error(`${format.toUpperCase()} export error:`, err);
      alert(t('common.error.exportFailed', {
        format: format.toUpperCase(),
        defaultValue: `Failed to export ${format.toUpperCase()} file. Please try again.`
      }));
    } finally {
      setExportLoading(prev => ({ ...prev, [format]: false }));
    }
  };

  // Helper function to translate status
  const getStatusTranslation = (status) => {
    switch (status) {
      case 'bidding':
        return t('status.bidding');
      case 'pending_allocation':
        return t('status.pending_allocation');
      case 'completed':
        return t('status.completed');
      case 'cancelled':
        return t('status.cancelled');
      default:
        return status.replace('_', ' ');
    }
  };

  const MobilePastVatCreditRequestCard = ({ request }) => (
    <div className="mobile-record-card">
      <div className="mobile-card-header">
        <div className="mobile-card-title">
          #{request.requestId}
        </div>
        <div className="mobile-card-status">
          <span className={`status status-${request.status}`}>
            {getStatusTranslation(request.status)}
          </span>
        </div>
      </div>

      <div className="mobile-card-body">
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.dateCreated')}</span>
          <span className="mobile-card-value">{new Date(request.createdAt).toLocaleDateString()}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('vatCredit.amount')}</span>
          <span className="mobile-card-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('vatCredit.indicativeDays')}</span>
          <span className="mobile-card-value">{request.indicativeDays} {t('common.days')}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('vatCredit.bidDeadline')}</span>
          <span className="mobile-card-value">{new Date(request.bidDeadline).toLocaleDateString()}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('bids.bidsReceived')}</span>
          <span className="mobile-card-value">{formatCurrencyWithSymbol(request.totalActiveBidAmount || 0, request.currency || 'EUR')}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('bids.totalAllocation')}</span>
          <span className="mobile-card-value">{formatCurrencyWithSymbol(request.totalAllocatedAmount || 0, request.currency || 'EUR')}</span>
        </div>
      </div>

      <div className="mobile-card-footer">
        <div className="mobile-card-date">
          {/* Space for additional info */}
        </div>
        <div className="mobile-card-action">
          <Link
            to={`/company/vat-credit/requests/${request.id}`}
            className="view-btn"
          >
            {t('common.view')}
          </Link>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="loading">{t('common.loadingRequests')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const STATUS_ORDER = { bidding: 0, pending_allocation: 1 };
  const sortedRequests = [...filteredRequests].sort((a, b) =>
    (STATUS_ORDER[a.status] ?? 2) - (STATUS_ORDER[b.status] ?? 2)
  );

  return (
    <div className="company-dashboard">
      <div className="tab-container">
        <div className="tabs">
          <div
            className={`tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTabChange('all')}
          >
            {t('requests.allRequests')}
          </div>
          <div
            className={`tab ${activeTab === 'bidding' ? 'active' : ''}`}
            onClick={() => handleTabChange('bidding')}
          >
            {t('requests.bidding')}
          </div>
          <div
            className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
            onClick={() => handleTabChange('completed')}
          >
            {t('requests.completed')}
          </div>
          <div
            className={`tab ${activeTab === 'cancelled' ? 'active' : ''}`}
            onClick={() => handleTabChange('cancelled')}
          >
            {t('status.cancelledPlural')}
          </div>
        </div>
      </div>

      <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
        <div className="card-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Files className="card-title-icon" size={20} />
            <span>{t('vatCredit.pastRequests')}</span>
          </div>

          {/* Export Actions */}
          {filteredRequests.length > 0 && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleExport('excel')}
                disabled={exportLoading.excel}
                className="btn btn-primary"
                style={{
                  fontSize: '12px',
                  padding: '2px 14px',
                  height: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  minWidth: '130px'
                }}
              >
                {exportLoading.excel ? (
                  <>
                    <div className="spinner" style={{ width: '10px', height: '10px' }}></div>
                    {t('common.exporting', { defaultValue: 'Exporting...' })}
                  </>
                ) : (
                  <>
                    {t('common.exportExcel', { defaultValue: 'Export Excel' })}
                  </>
                )}
              </button>
              <button
                onClick={() => handleExport('pdf')}
                disabled={exportLoading.pdf}
                className="btn btn-secondary"
                style={{
                  fontSize: '12px',
                  padding: '2px 14px !important',
                  height: '22px !important',
                  minHeight: '22px !important',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  minWidth: '130px'
                }}
              >
                {exportLoading.pdf ? (
                  <>
                    <div className="spinner" style={{ width: '10px', height: '10px' }}></div>
                    {t('common.exporting', { defaultValue: 'Exporting...' })}
                  </>
                ) : (
                  <>
                    {t('common.exportPdf', { defaultValue: 'Export PDF' })}
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Desktop Table */}
        {!isMobile && (
          <table>
            <thead>
              <tr>
                <th>{t('requests.requestId')}</th>
                <th>{t('requests.dateCreated')}</th>
                <th>{t('vatCredit.amount')}</th>
                <th>{t('vatCredit.indicativeDaysShort')}</th>
                <th>{t('vatCredit.bidDeadline')}</th>
                <th>{t('bids.bidsReceived')}</th>
                <th>{t('bids.totalAllocation')}</th>
                <th style={{ width: '120px', maxWidth: '120px' }}>{t('bids.status')}</th>
                <th>{t('common.action')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedRequests.length > 0 ? (
                sortedRequests.map(request => (
                  <tr key={request.id}>
                    <td>#{request.requestId}</td>
                    <td>{new Date(request.createdAt).toLocaleDateString()}</td>
                    <td>{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</td>
                    <td>{request.indicativeDays}</td>
                    <td>{new Date(request.bidDeadline).toLocaleDateString()}</td>
                    <td>{formatCurrencyWithSymbol(request.totalActiveBidAmount || 0, request.currency || 'EUR')}</td>
                    <td>{formatCurrencyWithSymbol(request.totalAllocatedAmount || 0, request.currency || 'EUR')}</td>
                    <td style={{ width: '120px', maxWidth: '120px' }}>
                      <span className={`status status-${request.status}`}>
                        {getStatusTranslation(request.status)}
                      </span>
                    </td>
                    <td>
                      <Link
                        to={`/company/vat-credit/requests/${request.id}`}
                        className="view-btn"
                      >
                        {t('common.view')}
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9">{t('requests.noRequestsFound')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* Mobile Cards */}
        {isMobile && (
          <div className="mobile-cards-container">
            {sortedRequests.length > 0 ? (
              sortedRequests.map(request => (
                <MobilePastVatCreditRequestCard key={request.id} request={request} />
              ))
            ) : (
              <div className="mobile-record-card mobile-empty-card">
                {t('requests.noRequestsFound')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PastVatCreditRequests;
